import { HStack, Icon, Text } from '@chakra-ui/react'
import { Tooltip } from '@infraloop/common'
import { useTranslation } from '@infraloop/i18n'
import { FileProcessingStatus, InspectionArea } from '@infraloop/services'
import { LuLoaderCircle } from 'react-icons/lu'

import { bytesToHumanReadable, getAreaSize } from '../../utils'

/**
 * The area sub-table's size cell, which doubles as the point-cloud processing status
 * (matching the legacy dashboard): a spinner while the backend converts the uploaded
 * file, an error with the backend's message on failure, 点群なし when the area has no
 * point cloud, and the file size otherwise.
 */
const AreaSizeCell = ({
  area,
}: {
  /** Inspection area the cell belongs to. */
  area: InspectionArea
}) => {
  // Hooks
  const { t } = useTranslation()

  // Derived state
  const status = area.downsample_status?.status

  if (status === FileProcessingStatus.Running) {
    return (
      <HStack justify="flex-end" pr="20px" gap="6px">
        <Icon
          as={LuLoaderCircle}
          boxSize="14px"
          color="primary.500"
          animationName="spin"
          animationDuration="0.8s"
          animationTimingFunction="linear"
          animationIterationCount="infinite"
        />
        <Text fontSize="13px" color="gray.500" data-testid="area-processing">
          {t('object.files.status.running')}...
        </Text>
      </HStack>
    )
  }

  if (status === FileProcessingStatus.Failed) {
    return (
      <Tooltip showArrow label={area.downsample_status?.status_message}>
        <Text fontSize="13px" color="red.600" textAlign="right" pr="20px" data-testid="area-processing-error">
          ⚠️ {t('object.files.status.failed')}
        </Text>
      </Tooltip>
    )
  }

  if (!area.origin_file?.name) {
    return (
      <Text fontSize="13px" color="gray.400" textAlign="right" pr="20px">
        {t('object.files.noPCD')}
      </Text>
    )
  }

  return (
    <Text fontSize="13px" color="gray.700" textAlign="right" pr="20px">
      {bytesToHumanReadable(getAreaSize(area))}
    </Text>
  )
}

export default AreaSizeCell
